import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { lighten, Theme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip
} from "@mui/material";

import { MoreVertRounded } from "@mui/icons-material";
import {
  gray1,
  lighestGray,
  lightGray,
  pprBorderColorA,
  strongGray,
  textColor,
  textGrayB,
  textGrayH1,
  toolbarBG1,
  white
} from "../DashStyleGuides";
import { GetTopStockPositions } from "../ApiServices/serviceCalls";

import { ProductStock } from "../../../models/product.stock";

interface Data {
  prodName: string;
  carbs: number;
  fat: number;
  sku: number;
  protein: number;
}

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      height: 34,
      borderBottom: `1px solid ${pprBorderColorA}`,
      borderRadius: "12px 12px 0px 0px"
    },
    highlight:
      theme.palette.mode === "light"
        ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85)
        }
        : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark
        },
    title: {
      flex: "1 1 100%",
      fontFamily: "Manrope",
      fontStyle: "normal",
      fontWeight: "bold",
      fontSize: 16,
      color: textGrayB
    }
  })
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: 300,
      border: "none",
      borderRadius: 12
    },
    paper: {
      width: "100%",
      height: "100%",
      marginBottom: theme.spacing(2),
      border: `1px solid ${pprBorderColorA}`,
      borderRadius: 12
    },
    table: {
      minWidth: 696,
      "& .tblrow": {
        height: 44,
        fontSize: 12,
        fontFamily: "Poppins",
        fontWeight: 400,
        color: textColor
      },
      "& .light": {
        background: white
      },
      "& .darker": { background: lightGray }
    },
    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1
    },
    tblHeaderRoot: {
      height: 32,
      "& .MuiTableCell-stickyHeader": {
        background: toolbarBG1
      },

      "& th": {
        fontSize: 12,
        fontWeight: 700,
        color: textGrayH1
      }
    },
    lblEmpty: {
      display: "block",
      margin: "auto",
      marginTop: 80,
      color: pprBorderColorA,
      fontWeight: 400
    }
  })
);

type Order = "asc" | "desc";

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  {
    id: "sku",
    numeric: true,
    disablePadding: true,
    label: "SKU"
  },
  {
    id: "description",
    numeric: true,
    disablePadding: false,
    label: "Product Name"
  },
  {
    id: "warehouse",
    numeric: false,
    disablePadding: false,
    label: "Warehouse"
  },
  {
    id: "quantity",
    numeric: true,
    disablePadding: false,
    label: "Quantity"
  },
  { id: "weight", numeric: true, disablePadding: false, label: "Weight" },
  { id: "width", numeric: true, disablePadding: false, label: "Width" },
  { id: "height", numeric: true, disablePadding: false, label: "Height" }
];

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Order;
  orderBy: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy } = props;

  const cssClasses = useStyles();

  return (
    <TableHead className={cssClasses.tblHeaderRoot}>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align="center"
            padding="none"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const EnhancedTableToolbar = () => {
  const classes = useToolbarStyles();

  return (
    <Toolbar className={classes.root} variant="dense">
      <Typography className={classes.title} id="tableTitle" component="div">
        Stock Position
      </Typography>
    </Toolbar>
  );
};

export default function StockPosition() {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("prodName");

  const [topStocksList, setStocksList] = useState<ProductStock[]>([]);

  useEffect(() => {
    getTopList();
  }, []);

  const getTopList = async () => {
    const stockPositions: any = await GetTopStockPositions();
    if (stockPositions) {
      setStocksList(
        stockPositions !== undefined ? stockPositions.data : undefined
      );
    }
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Grid item sm={12} md={8} xs={12}>
      <div className={classes.root}>
        <Paper className={classes.paper} square>
          <EnhancedTableToolbar />
          <TableContainer style={{ maxHeight: 250 }}>
            <Table
              stickyHeader
              className={classes.table}
              aria-labelledby="tableTitle"
              size="small"
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />

              <TableBody>
                {topStocksList.length > 0 &&
                  topStocksList.map((info, index) => (
                    <TableRow
                      className={`tblrow ${index % 2 == 0 ? "light" : "darker"
                        }`}
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                    >
                      <TableCell
                        id={info?.id?.toString() || index.toString()}
                        scope="row"
                        padding="none"
                        align="center"
                      >
                        {info?.product?.sku}
                      </TableCell>
                      <TableCell padding="none" align="center">
                        {info?.product?.description}
                      </TableCell>
                      <TableCell padding="none" align="center">
                        {info?.warehouse?.name}
                      </TableCell>
                      <TableCell align="center">
                        {info?.onHand}
                      </TableCell>
                      <TableCell align="center">
                        {(info?.product?.weight) ? info?.product?.weight : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {(info?.product?.width) ? info?.product?.width : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {(info?.product?.height) ? info?.product?.height : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {topStocksList.length === 0 && (
              <Typography
                className={classes.lblEmpty}
                variant="h4"
                align="center"
              >
                No Data
              </Typography>
            )}
          </TableContainer>
        </Paper>
      </div>
    </Grid>
  );
}
